import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const CalendarMonthView = ({
  calendarData,
  selectedDate,
  isSameDay,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getShipmentBackgroundColor,
  getShipmentStatusText,
  getTransportIcon,
  handleShipmentClick,
  setHoveredReference,
  hoveredReference,
}) => {
  // Generate empty cells for days before the first of the month
  const generateLeadingEmptyCells = () => {
    const firstDayOfMonth = getFirstDayOfMonth(selectedDate);
    const dayOfWeek = firstDayOfMonth.getDay();
    // Adjust for Monday as first day of week (0 = Sunday, 1 = Monday, etc.)
    const emptyCellCount = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    return Array.from({ length: emptyCellCount }).map((_, index) => (
      <div
        key={`empty-start-${index}`}
        className="bg-gray-50 border p-2 min-h-[100px]"
      ></div>
    ));
  };

  // Generate empty cells for days after the last of the month
  const generateTrailingEmptyCells = () => {
    const lastDayOfMonth = getLastDayOfMonth(selectedDate);
    const dayOfWeek = lastDayOfMonth.getDay();
    // Calculate trailing cells (if last day is Sunday, we need 0 cells, otherwise 7 - day)
    const emptyCellCount = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

    return Array.from({ length: emptyCellCount }).map((_, index) => (
      <div
        key={`empty-end-${index}`}
        className="bg-gray-50 border p-2 min-h-[100px]"
      ></div>
    ));
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Month view header (days of week) */}
      <div className="grid grid-cols-7 bg-gray-100">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-700 border-r last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {/* Empty cells before first day of month */}
        {generateLeadingEmptyCells()}

        {/* Actual calendar days */}
        {calendarData.map((day) => (
          <div
            key={day.date.toISOString()}
            className={`border p-2 min-h-[100px] ${
              isSameDay(day.date, new Date()) ? "bg-blue-50" : ""
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <span
                className={`text-sm font-medium ${
                  isSameDay(day.date, new Date())
                    ? "bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center"
                    : ""
                }`}
              >
                {day.date.getDate()}
              </span>
              {day.shipments.length > 0 && (
                <span className="text-xs bg-gray-100 rounded-full px-2 py-0.5">
                  {day.shipments.length}
                </span>
              )}
            </div>

            {/* Shipments for this day */}
            <div className="space-y-1 overflow-y-auto max-h-[180px]">
              {day.shipments.slice(0, 5).map((shipment) => (
                <div
                  key={shipment.header.reference}
                  className={`p-1 rounded text-xs cursor-pointer ${getShipmentBackgroundColor(
                    shipment
                  )} hover:shadow-md transition-shadow`}
                  onMouseEnter={() =>
                    setHoveredReference(shipment.header.reference)
                  }
                  onMouseLeave={() => setHoveredReference(null)}
                  onClick={() => handleShipmentClick(shipment)}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium truncate">
                      {shipment.header.reference}
                    </span>
                    <FontAwesomeIcon
                      icon={
                        getTransportIcon(
                          shipment.header.pedimento?.pedimento_entry_point
                        ).icon
                      }
                      className={
                        getTransportIcon(
                          shipment.header.pedimento?.pedimento_entry_point
                        ).color
                      }
                    />
                  </div>
                  {hoveredReference === shipment.header.reference && (
                    <div className="mt-1 text-xs">
                      <div className="truncate">
                        {shipment.header.customer_name}
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>{getShipmentStatusText(shipment)}</span>
                        <span className="truncate">
                          {shipment.header.pedimento?.pedimento_number}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {day.shipments.length > 5 && (
                <div className="text-xs text-center text-gray-500 mt-1">
                  +{day.shipments.length - 5} more
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Empty cells after last day of month */}
        {generateTrailingEmptyCells()}
      </div>
    </div>
  );
};

export default CalendarMonthView;
